import { useEffect, useState, useRef } from "react";
import { motion, useInView } from "framer-motion";

interface StatItemProps {
  value: number;
  suffix?: string;
  prefix?: string;
  label: string;
  delay?: number;
}

const useCountUp = (end: number, duration: number = 2000, start: boolean = true, decimals: number = 0) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;

    let startTime: number;
    let animationFrame: number;
    const factor = Math.pow(10, decimals);

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.round(easeOutQuart * end * factor) / factor);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, start, decimals]);

  return count;
};

const StatItem = ({ value, suffix = "", prefix = "", label, delay = 0, decimals = 0 }: StatItemProps & { decimals?: number }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const count = useCountUp(value, 2500, isInView, decimals);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay }}
      className="text-center"
    >
      <div className="text-3xl md:text-4xl font-extrabold text-foreground mb-1 tabular-nums tracking-tight">
        {prefix}{decimals > 0 ? count.toFixed(decimals).replace('.', ',') : count.toLocaleString('pt-BR')}{suffix}
      </div>
      <p className="text-muted-foreground text-xs font-medium uppercase tracking-widest">{label}</p>
    </motion.div>
  );
};

const StatsCounter = () => {
  return (
    <section className="py-12 bg-background border-y border-border/40">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
          <StatItem value={300} suffix="+" label="Parceiros" delay={0} />
          <StatItem value={1700} suffix="+" label="Contratos" delay={0.1} />
          <StatItem prefix="R$ " value={1.5} suffix="M+" label="Comissões Pagas" delay={0.2} decimals={1} />
          <StatItem value={70} suffix="+" label="Instituições" delay={0.3} />
        </div>
      </div>
    </section>
  );
};

export default StatsCounter;
